import React from 'react';
import { Eye, EyeOff } from 'lucide-react';
import { useDemoMode } from '../../../context/DemoModeContext';
import Breadcrumbs from '../../common/Breadcrumbs';
import './SettingsDetail.css';

const maskedFields = [
  { label: 'Route & Grouping Names', example: '"Northpoint Depot to Lakeside Hub"' },
  { label: 'Location Names', example: '"Site Northpoint", "Site Lakeside"' },
  { label: 'Company & Customer Names', example: '"Acme Logistics", "Atlas Freight"' },
  { label: 'Ticket & Order IDs', example: '"DM-A1B2C3D4"' },
  { label: 'Vehicle Registrations', example: '"ABC 123 GP"' },
  { label: 'Product Names', example: '"Material A", "Commodity Alpha"' },
  { label: 'Person Names', example: '"J. Smith", "A. Johnson"' },
  { label: 'Email Addresses', example: '"user1@demo.example.com"' },
];

const breadcrumbItems = [{ label: 'Presentation' }, { label: 'Demo Mode' }];

const DemoModeSettings = () => {
  const { isDemoMode, toggleDemoMode } = useDemoMode();

  return (
    <div className="settings-detail">
      <Breadcrumbs items={breadcrumbItems} />

      <div className="settings-detail-container">
        <div className="settings-detail-header">
          <div
            className="settings-detail-header-icon"
            style={{
              background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
              boxShadow: '0 2px 8px -2px rgba(245, 158, 11, 0.4)',
            }}
          >
            <Eye size={26} />
          </div>
          <div>
            <h1 className="settings-detail-title">Demo Mode</h1>
            <p className="settings-detail-description">
              Mask sensitive data for client presentations and demos. Real data is never modified —
              masking is applied visually in the browser only.
            </p>
          </div>
        </div>

        <div className="settings-detail-content">
          {/* Toggle Card */}
          <div className="settings-card-detail">
            <div className="settings-card-header-detail">
              {isDemoMode ? <EyeOff size={20} /> : <Eye size={20} />}
              <h2>Data Masking</h2>
            </div>

            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: '1.5rem',
              }}
            >
              <div>
                <p
                  style={{
                    margin: 0,
                    fontWeight: 600,
                    color: 'var(--text-strong)',
                    fontSize: '0.875rem',
                  }}
                >
                  Enable Demo Mode
                </p>
                <p
                  style={{
                    margin: '0.25rem 0 0',
                    color: 'var(--text-muted)',
                    fontSize: '0.8125rem',
                    lineHeight: 1.5,
                  }}
                >
                  When enabled, route names, locations, companies, ticket IDs, and other identifying
                  data will be replaced with generic placeholders in all pages.
                </p>
              </div>
              <button
                onClick={toggleDemoMode}
                type="button"
                role="switch"
                aria-checked={isDemoMode}
                style={{
                  position: 'relative',
                  flexShrink: 0,
                  width: 48,
                  height: 26,
                  borderRadius: 13,
                  border: 'none',
                  cursor: 'pointer',
                  background: isDemoMode
                    ? 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)'
                    : 'var(--border-strong)',
                  transition: 'background 200ms ease',
                }}
              >
                <span
                  style={{
                    position: 'absolute',
                    top: 3,
                    borderRadius: '50%',
                    width: 20,
                    height: 20,
                    background: '#fff',
                    boxShadow: '0 1px 3px rgb(var(--shadow-rgb) / 0.2)',
                    transition: 'left 200ms ease',
                    left: isDemoMode ? 25 : 3,
                  }}
                />
              </button>
            </div>

            {isDemoMode && (
              <div
                className="settings-alert settings-alert-warning"
                style={{ marginTop: '1rem', marginBottom: 0 }}
              >
                Demo mode is active. Navigate to any page to see masked data. Toggling off will
                restore real data on the next page load or data refresh.
              </div>
            )}
          </div>

          {/* What Gets Masked */}
          <div className="settings-card-detail">
            <div className="settings-card-header-detail">
              <h2>What Gets Masked</h2>
            </div>
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
                gap: '0.75rem',
              }}
            >
              {maskedFields.map((item) => (
                <div
                  key={item.label}
                  style={{
                    padding: '0.75rem',
                    background: 'var(--surface-sunken)',
                    borderRadius: '0.5rem',
                    border: '1px solid var(--border-subtle)',
                  }}
                >
                  <p
                    style={{
                      margin: 0,
                      fontWeight: 600,
                      fontSize: '0.8125rem',
                      color: 'var(--text)',
                    }}
                  >
                    {item.label}
                  </p>
                  <p
                    style={{
                      margin: '0.25rem 0 0',
                      fontSize: '0.75rem',
                      color: 'var(--text-faint)',
                      fontStyle: 'italic',
                    }}
                  >
                    {item.example}
                  </p>
                </div>
              ))}
            </div>
            <p style={{ marginTop: '1rem', fontSize: '0.8125rem', color: 'var(--text-muted)' }}>
              Weights, counts, dates, and percentage values are <strong>not</strong> masked — charts
              and trends will still look realistic with real figures.
            </p>
          </div>

          {/* Info Card */}
          <div
            className="settings-info-card"
            style={{
              background: 'var(--warning-soft)',
              borderColor: 'var(--warning-border)',
            }}
          >
            <h3 className="settings-info-title" style={{ color: 'var(--warning-text)' }}>
              How It Works
            </h3>
            <ul className="settings-info-list">
              <li style={{ color: 'var(--warning-text)' }}>
                Masking is applied in the browser — no data is changed on the server or database
              </li>
              <li style={{ color: 'var(--warning-text)' }}>
                The same original value always maps to the same masked value within a session
              </li>
              <li style={{ color: 'var(--warning-text)' }}>
                Enable demo mode before navigating to pages you want to present
              </li>
              <li style={{ color: 'var(--warning-text)' }}>
                An amber banner appears at the top of every page while demo mode is active
              </li>
              <li style={{ color: 'var(--warning-text)' }}>
                Demo mode resets when the browser tab is closed
              </li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DemoModeSettings;
